import { z } from 'zod';
import { FilterOptionType } from 'src/components/RealityCheckForm/CustomDropdown';

export type GamstopPpPrimaryActionType =
    | 'blocked'
    | 'suspended'
    | 'apply-custom-limit'
    | 'deposit-restriction'
    | 'compliance-restriction';

export type GamstopPpActionExecutionOrderKeyType = GamstopPpPrimaryActionType | 'SendEmail' | 'ShowPopup';

export const GAMSTOP_PP_DEFAULT_EXECUTION_ORDERS: Record<GamstopPpActionExecutionOrderKeyType, number> = {
    blocked: 1,
    suspended: 2,
    'apply-custom-limit': 3,
    'deposit-restriction': 4,
    'compliance-restriction': 5,
    SendEmail: 6,
    ShowPopup: 7,
};

export type GamstopPpCommunicationOnlyModeType = 'send-email-only' | 'send-popup-only' | 'send-email-and-popup';

export type GamstopPpSecondaryActionType = 'send-communication-only' | 'no-action';

export type GamstopPpAutomatedActionType = GamstopPpPrimaryActionType | GamstopPpSecondaryActionType;

export const gamstopPpSetAccountStatusActionSchema = z.enum([
    'blocked',
    'suspended',
    'deposit-restriction',
    'compliance-restriction',
]);

export type GamstopPpSetAccountStatusActionType = z.infer<typeof gamstopPpSetAccountStatusActionSchema>;

export type GamstopPpAutomatedActionOptionType = {
    value: GamstopPpAutomatedActionType;
    label: string;
    description: string;
};

export type GamstopPpPrimaryActionOptionType = {
    value: GamstopPpPrimaryActionType;
    label: string;
    description: string;
};

export const GAMSTOP_PP_PRIMARY_ACTIONS: Array<GamstopPpPrimaryActionOptionType> = [
    {
        value: 'blocked',
        label: 'Blocked',
        description: 'Set the account status to Blocked when a user returns as Previously Positive.',
    },
    {
        value: 'suspended',
        label: 'Suspended',
        description: 'Set the account status to Suspended when a user returns as Previously Positive.',
    },
    {
        value: 'apply-custom-limit',
        label: 'Apply custom limit',
        description: 'Apply a configured net deposit limit when a user returns as Previously Positive.',
    },
    {
        value: 'deposit-restriction',
        label: 'Deposit Restriction',
        description: 'Apply a deposit restriction when a user returns as Previously Positive.',
    },
    {
        value: 'compliance-restriction',
        label: 'Compliance Restriction',
        description: 'Apply a compliance restriction when a user returns as Previously Positive.',
    },
];

export const GAMSTOP_PP_COMMUNICATION_ONLY_MODE_OPTIONS: Array<FilterOptionType<GamstopPpCommunicationOnlyModeType>> = [
    { value: 'send-email-only', label: 'Email only' },
    { value: 'send-popup-only', label: 'Popup only' },
    { value: 'send-email-and-popup', label: 'Both' },
];

export const GAMSTOP_PP_SECONDARY_ACTIONS: Array<GamstopPpAutomatedActionOptionType> = [
    {
        value: 'send-communication-only',
        label: 'Send communication only',
        description: 'Send communication without changing account status or applying restrictions.',
    },
    {
        value: 'no-action',
        label: 'No action',
        description: 'Disable all automated actions when a user returns as Previously Positive.',
    },
];

export const GAMSTOP_PP_ACTIONS: Array<GamstopPpAutomatedActionOptionType> = [
    ...GAMSTOP_PP_PRIMARY_ACTIONS,
    ...GAMSTOP_PP_SECONDARY_ACTIONS,
];

const isBlockedOrSuspendedAction = (action: GamstopPpAutomatedActionType): boolean => {
    return action === 'blocked' || action === 'suspended';
};

export const getGamstopPpVisibleActions = (
    includeBlockedAndSuspended: boolean
): Array<GamstopPpAutomatedActionOptionType> => {
    if (includeBlockedAndSuspended) {
        return GAMSTOP_PP_ACTIONS;
    }

    return GAMSTOP_PP_ACTIONS.filter((option) => !isBlockedOrSuspendedAction(option.value));
};

export const getGamstopPpAutomatedActionOption = (
    action: GamstopPpAutomatedActionType
): GamstopPpAutomatedActionOptionType | undefined => {
    return GAMSTOP_PP_ACTIONS.find((option) => option.value === action);
};

export const isApplyCustomLimitAction = (action: GamstopPpAutomatedActionType | null): boolean => {
    return action === 'apply-custom-limit';
};

export const isComplianceRestrictionAction = (action: GamstopPpAutomatedActionType | null): boolean => {
    return action === 'compliance-restriction';
};

export const isDepositRestrictionAction = (action: GamstopPpAutomatedActionType | null): boolean => {
    return action === 'deposit-restriction';
};

export const isSendCommunicationOnlyAction = (action: GamstopPpAutomatedActionType | null): boolean => {
    return action === 'send-communication-only';
};

export const isNoAction = (action: GamstopPpAutomatedActionType | null): boolean => {
    return action === 'no-action';
};

export const isCommunicationOnlyAction = (action: GamstopPpAutomatedActionType | null): boolean => {
    return isSendCommunicationOnlyAction(action);
};

export const isPrimaryGamstopPpAction = (
    action: GamstopPpAutomatedActionType | null
): action is GamstopPpPrimaryActionType => {
    return action !== null && !isSendCommunicationOnlyAction(action) && !isNoAction(action);
};
